import { FC, useEffect, useState } from "react";
import { Button } from "../Button";
import { List } from "../List";
import { Tag } from "../Tag";
import { Typography } from "../Typography";
import { Upload } from "../Upload";
import { message } from "../message";
import { UndoOutlined } from "@ant-design/icons";
import { ReactComponent as CloseIcon } from "../../../assets/icons/svg/close.svg";
import { ReactComponent as DocumentIcon } from "../../../assets/icons/svg/document.svg";
import { DocumentCard } from "./DocumentCard";
import styles from "./DocumentList.module.less";

export type DocumentChange = "added" | "removed";

export interface DocumentItem {
    id: number | string;
    name: string;
    change?: DocumentChange;
    file?: File;
    uploadedBy?: string;
    uploadedAt?: Date | string;
}

export interface DocumentListProps {
    documents: DocumentItem[];
    allowedExtensions?: string[];
    readOnly?: boolean;
    uploaderName?: string;
    onChange?: (documents: DocumentItem[]) => void;
}

/**
 * Editor for a list of documents.
 *
 * Read-only mode: shows documents with their change flags (added/removed) as provided by the parent.
 * 
 * Edit mode: documents prop has no change flags. The component tracks user actions
 * (adding/removing) locally and reports the full list via onChange.
 */
export const DocumentList: FC<DocumentListProps> = ({
    documents,
    allowedExtensions,
    readOnly,
    uploaderName,
    onChange,
}) => {
    const [items, setItems] = useState<DocumentItem[]>(documents);

    useEffect(() => {
        setItems(documents);
    }, [documents]);

    const update = (next: DocumentItem[]) => {
        setItems(next);
        onChange?.(next);
    };

    const handleRemove = (docId: number | string) => {
        update(items.map((d) => d.id === docId ? { ...d, change: "removed" as const } : d));
    };

    const handleRestore = (docId: number | string) => {
        update(items.map((d) => d.id === docId ? { ...d, change: undefined } : d));
    };

    const handleRemoveNew = (docId: number | string) => {
        update(items.filter((d) => d.id !== docId));
    };

    const handleFileSelect = (file: File, fileList: File[]) => {
        // Ant Design calls beforeUpload once per file. Only process on the last
        // file so we add the entire batch in a single state update.
        if (file !== fileList[fileList.length - 1]) return false;

        const newDocs: DocumentItem[] = [];
        for (const f of fileList) {
            if (allowedExtensions) {
                const ext = f.name.substring(f.name.lastIndexOf(".")).toLowerCase();
                if (!allowedExtensions.includes(ext)) {
                    message.error(
                        `Unsupported file type: ${f.name}. Allowed: ${allowedExtensions.join(", ")}`
                    );
                    continue;
                }
            }
            newDocs.push({
                id: `new-${Date.now()}-${Math.random().toString(36).slice(2)}-${f.name}`,
                name: f.name,
                change: "added",
                file: f,
                uploadedBy: uploaderName,
                uploadedAt: new Date(),
            });
        }

        if (newDocs.length > 0) {
            setItems((prev) => {
                const next = [...prev, ...newDocs];
                onChange?.(next);
                return next;
            });
        }
        return false;
    };

    // === Read-only mode ===

    if (readOnly) {
        if (documents.length === 0) return null;

        return (
            <List className={styles.documentList} size="small">
                {documents.map((doc) => (
                    <List.Item
                        key={doc.id}
                        actions={[
                            ...(doc.change === "added" ? [<Tag color="green">Added</Tag>] : []),
                            ...(doc.change === "removed" ? [<Tag color="red">Removed</Tag>] : []),
                        ]}
                    >
                        <div style={{
                            textDecoration: doc.change === "removed" ? "line-through" : undefined,
                            opacity: doc.change === "removed" ? 0.45 : 1,
                        }}>
                            <DocumentCard
                                name={doc.name}
                                uploadedBy={doc.uploadedBy}
                                uploadedAt={doc.uploadedAt}
                            />
                        </div>
                    </List.Item>
                ))}
            </List>
        );
    }

    // === Editable mode ===

    return (
        <>
            {items.length > 0 && (
                <List className={styles.documentList} size="small">
                    {items.map((doc) => (
                        <List.Item
                            key={doc.id}
                            actions={[
                                ...(doc.change === "added" ? [<Tag color="green">ADDED</Tag>] : []),
                                ...(doc.change === "removed" ? [<Tag color="red">REMOVED</Tag>] : []),
                                doc.change === "removed" ? (
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={<UndoOutlined />}
                                        onClick={() => handleRestore(doc.id)}
                                    />
                                ) : doc.change === "added" ? (
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={<CloseIcon style={{ width: 10, height: 10 }} />}
                                        onClick={() => handleRemoveNew(doc.id)}
                                    />
                                ) : (
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={<CloseIcon style={{ width: 10, height: 10 }} />}
                                        onClick={() => handleRemove(doc.id)}
                                    />
                                ),
                            ]}
                        >
                            <div style={{
                            textDecoration: doc.change === "removed" ? "line-through" : undefined,
                            opacity: doc.change === "removed" ? 0.45 : 1,
                        }}>
                            <DocumentCard
                                name={doc.name}
                                uploadedBy={doc.uploadedBy}
                                uploadedAt={doc.uploadedAt}
                            />
                        </div>
                        </List.Item>
                    ))}
                </List>
            )}
            <Upload.Dragger
                multiple
                beforeUpload={handleFileSelect}
                fileList={[]}
                accept={allowedExtensions?.join(",")}
                className={styles.dragger}
            >
                <div style={{ textAlign: "center" }}>
                    <DocumentIcon style={{ width: 32, height: 32, marginBottom: 8 }} />
                    <div>
                        <Typography.Text type="secondary">
                            Drop or click to add files
                        </Typography.Text>
                    </div>
                </div>
            </Upload.Dragger>
        </>
    );
};
